import { createServerFn } from "@tanstack/react-start";
import { getRequestHeaders } from "@tanstack/react-start/server";
import { eq } from "drizzle-orm";
import z from "zod";

import { lower } from "@/integrations/drizzle/helpers";
import { user } from "@/integrations/drizzle/schema";
import { runActivationResendWithProtection } from "@/integrations/multiresume/access/abuse-enforcement";
import { mapUserActivation } from "@/integrations/multiresume/access/activation";
import { resendActivation } from "@/integrations/multiresume/access/activation-resend";

import {
  createSupabaseAdminClient,
  getSupabaseAuthSession,
  resendSupabaseSignupConfirmation,
  resetSupabasePasswordForEmail,
  signInWithSupabasePassword,
  signOutSupabase,
  updateSupabasePassword,
} from "./supabase";
import { registerWithSupabaseInvite, supabaseInviteRegistrationInputSchema } from "./supabase-registration";

const signInSchema = z.object({
  email: z.email().trim().toLowerCase(),
  password: z.string().min(1),
});

const emailSchema = z.object({
  email: z.email().trim().toLowerCase(),
});

const activationResendSchema = emailSchema.extend({ challengeToken: z.string().min(1).max(4096) });

const passwordSchema = z.object({
  password: z.string().min(8).max(64),
  acceptedTermsAndPrivacy: z.boolean(),
  mode: z.enum(["invite", "recovery"]).optional(),
});

export const registerWithSupabaseInviteServerFn = createServerFn({ method: "POST" })
  .inputValidator(supabaseInviteRegistrationInputSchema)
  .handler(async ({ data }) => {
    const reqHeaders = new Headers(getRequestHeaders());
    reqHeaders.set("x-captcha-response", data.challengeToken);
    return await registerWithSupabaseInvite({
      invitationCode: data.invitationCode,
      email: data.email,
      username: data.username,
      password: data.password,
      usernameDefaultUntouched: data.usernameDefaultUntouched,
      reqHeaders,
    });
  });

export const signInWithSupabasePasswordServerFn = createServerFn({ method: "POST" })
  .inputValidator(signInSchema)
  .handler(async ({ data }) => {
    return await signInWithSupabasePassword(data);
  });

export const getSupabaseAuthSessionServerFn = createServerFn({ method: "GET" }).handler(async () => {
  return await getSupabaseAuthSession();
});

export const resetSupabasePasswordForEmailServerFn = createServerFn({ method: "POST" })
  .inputValidator(emailSchema)
  .handler(async ({ data }) => {
    return await resetSupabasePasswordForEmail(data.email);
  });

export const resendSupabaseActivationServerFn = createServerFn({ method: "POST" })
  .inputValidator(activationResendSchema)
  .handler(async ({ data }) => {
    const { db } = await import("@/integrations/drizzle/client");
    const [profile] = await db
      .select()
      .from(user)
      .where(eq(lower(user.email), data.email))
      .limit(1);
    if (!profile) return { result: "provider_error" as const };

    const admin = createSupabaseAdminClient();
    const { data: authData, error } = await admin.auth.admin.getUserById(profile.id);
    const authUser = authData.user;
    if (
      error ||
      !authUser ||
      authUser.email?.trim().toLowerCase() !== data.email ||
      authUser.email_confirmed_at ||
      authUser.confirmed_at
    ) {
      return { result: "provider_error" as const };
    }

    const reqHeaders = new Headers(getRequestHeaders());
    reqHeaders.set("x-captcha-response", data.challengeToken);
    return await runActivationResendWithProtection(
      { reqHeaders },
      { email: data.email, userId: profile.id },
      async () => ({
        result: await resendActivation(
          {
            userId: profile.id,
            email: profile.email,
            activationState: mapUserActivation(profile),
          },
          {
            adapter: {
              sendActivationEmail: async ({ email }) => await resendSupabaseSignupConfirmation(email),
            },
          },
        ),
      }),
    );
  });

export const updateSupabasePasswordServerFn = createServerFn({ method: "POST" })
  .inputValidator(passwordSchema)
  .handler(async ({ data }) => {
    return await updateSupabasePassword(data);
  });

export const signOutSupabaseServerFn = createServerFn({ method: "POST" }).handler(async () => {
  await signOutSupabase();
});
